import React, { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  Box,
  Typography,
  Autocomplete,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { UserInvitationCreate } from "../../types/registrationTypes";
import { BotTokenCreate } from "../../types/botTypes";
import { useTSMCreateBotToken } from "../../hooks/useTSBots";

// Validation schema
const botCreateSchema = yup.object({
  name: yup.string().required("Name is Required"),
  description: yup.string().required("Description is Required"),
  permissions: yup.array().of(yup.string()).min(0),
});

interface CreateBotFormData {
  name: string;
  description: string;
  permissions: string[];
}

interface CreateBotFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateBotForm: React.FC<CreateBotFormProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  // State
  const [error, setError] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);

  // Invite user mutation
  const createBotMutation = useTSMCreateBotToken();

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<CreateBotFormData>({
    resolver: yupResolver(botCreateSchema) as any,
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
    },
  });

  // Form submission
  const onSubmit = async (data: CreateBotFormData) => {
    try {
      setError(null);
      setNewToken(null);

      const invitationData: BotTokenCreate = {
        name: data.name,
        description: data.description,
        permissions: [],
      };

      const result = await createBotMutation.mutateAsync(invitationData);

      setNewToken(`Token Created: ${result.token}`);
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      setError(err.message || "Failed to send invitation");
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isSubmitting && !createBotMutation.isPending) {
      reset();
      setError(null);
      setNewToken(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: 600 },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon />
            <Typography variant="h6">Create Bot Token</Typography>
          </Stack>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Success Alert */}
          {newToken && <Alert severity="success">{newToken}</Alert>}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Instructions */}
          <Box>
            <Typography variant="body1" gutterBottom>
              Create a unique token to use for other apps to use for
              authentication.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <TextField
                    {...restField}
                    value={value}
                    onChange={onChange}
                    label="Enter Token Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                )}
              />

              {/* Email Field */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    required
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Stack>
          </form>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting || createBotMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting || createBotMutation.isPending}
          startIcon={<PersonAddIcon />}
        >
          {isSubmitting || createBotMutation.isPending
            ? "Creating Bot Token..."
            : "Create Bot Token"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
